import { useState } from 'react';
import type { AppLockInstance } from '@ascend/applock';
import { Lock } from 'lucide-react';

interface WelcomeScreenProps {
  onEnable: () => void;
  onSkip: () => void;
}

export function WelcomeScreen({ onEnable, onSkip }: WelcomeScreenProps) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', padding: 'var(--space-xl)',
      background: 'var(--bg-primary)',
    }}>
      <div style={{
        maxWidth: 420, width: '100%', textAlign: 'center',
        padding: 'var(--space-2xl)', borderRadius: 'var(--border-radius-lg)',
        background: 'var(--bg-elevated)', boxShadow: 'var(--shadow-lg)',
      }}>
        <div style={{ fontSize: 'var(--font-size-3xl)', marginBottom: 'var(--space-md)' }}><Lock size={32} /></div>
        <h1 style={{ fontSize: 'var(--font-size-2xl)', marginBottom: 'var(--space-sm)', color: 'var(--text-primary)' }}>
          Protect Your Data
        </h1>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-xl)', lineHeight: 'var(--line-height-relaxed)' }}>
          Slot Matrix stores your warehouse data locally. Enable password protection
          to encrypt your sessions with AES-256-GCM.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
          <button onClick={onEnable} style={{
            padding: 'var(--space-md) var(--space-xl)',
            background: 'var(--color-primary)', color: 'white',
            borderRadius: 'var(--border-radius-md)', fontWeight: 'var(--font-weight-semibold)',
            fontSize: 'var(--font-size-lg)', cursor: 'pointer',
          }}>
            Enable Protection
          </button>
          <button onClick={onSkip} style={{
            padding: 'var(--space-md) var(--space-xl)',
            background: 'transparent', color: 'var(--text-secondary)',
            borderRadius: 'var(--border-radius-md)', border: '1px solid var(--border-color)',
            cursor: 'pointer',
          }}>
            Skip for Now
          </button>
        </div>
      </div>
    </div>
  );
}

interface SetupScreenProps {
  onSubmit: (password: string) => Promise<void>;
  onSkip: () => void;
}

export function SetupScreen({ onSubmit, onSkip }: SetupScreenProps) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setSubmitting(true);
    try { await onSubmit(password); } catch (err) { setError(String(err)); } finally { setSubmitting(false); }
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', padding: 'var(--space-xl)', background: 'var(--bg-primary)',
    }}>
      <form onSubmit={handleSubmit} style={{
        maxWidth: 380, width: '100%', padding: 'var(--space-2xl)',
        borderRadius: 'var(--border-radius-lg)', background: 'var(--bg-elevated)',
        boxShadow: 'var(--shadow-lg)',
      }}>
        <h2 style={{ fontSize: 'var(--font-size-xl)', marginBottom: 'var(--space-md)', color: 'var(--text-primary)' }}>
          Create Password
        </h2>
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-warning)', marginBottom: 'var(--space-md)' }}>
          This password cannot be recovered. If you forget it, your encrypted data will be permanently inaccessible.
        </p>
        {error && <div style={{ color: 'var(--color-error)', marginBottom: 'var(--space-sm)', fontSize: 'var(--font-size-sm)' }}>{error}</div>}
        <div style={{ marginBottom: 'var(--space-md)' }}>
          <label htmlFor="setup-password" style={{ display: 'block', marginBottom: 4, fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>Password</label>
          <input id="setup-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
            style={{ width: '100%', padding: 'var(--space-sm)', borderRadius: 'var(--border-radius-sm)', border: '1px solid var(--border-color)', boxSizing: 'border-box' }} />
        </div>
        <div style={{ marginBottom: 'var(--space-lg)' }}>
          <label htmlFor="setup-confirm" style={{ display: 'block', marginBottom: 4, fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>Confirm Password</label>
          <input id="setup-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6}
            style={{ width: '100%', padding: 'var(--space-sm)', borderRadius: 'var(--border-radius-sm)', border: '1px solid var(--border-color)', boxSizing: 'border-box' }} />
        </div>
        <button type="submit" disabled={submitting || !password || !confirm} style={{
          width: '100%', padding: 'var(--space-md)', background: 'var(--color-primary)', color: 'white',
          borderRadius: 'var(--border-radius-md)', fontWeight: 'var(--font-weight-semibold)',
          cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1,
        }}>
          {submitting ? 'Setting up...' : 'Create Password'}
        </button>
        <button type="button" onClick={onSkip} style={{
          width: '100%', padding: 'var(--space-md)', marginTop: 'var(--space-sm)',
          background: 'transparent', color: 'var(--text-secondary)', border: 'none', cursor: 'pointer',
        }}>
          Skip
        </button>
      </form>
    </div>
  );
}

interface LockScreenProps {
  onUnlock: (password: string) => Promise<boolean>;
}

export function LockScreen({ onUnlock }: LockScreenProps) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [unlocking, setUnlocking] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUnlocking(true);
    setError('');
    try {
      const ok = await onUnlock(password);
      if (!ok) setError('Wrong password');
    } catch { setError('Unlock failed'); } finally { setUnlocking(false); }
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', padding: 'var(--space-xl)', background: 'var(--bg-primary)',
    }}>
      <form onSubmit={handleSubmit} style={{
        maxWidth: 340, width: '100%', padding: 'var(--space-2xl)',
        borderRadius: 'var(--border-radius-lg)', background: 'var(--bg-elevated)',
        boxShadow: 'var(--shadow-lg)', textAlign: 'center',
      }}>
        <div style={{ fontSize: 'var(--font-size-3xl)', marginBottom: 'var(--space-md)' }}><Lock size={32} /></div>
        <h2 style={{ fontSize: 'var(--font-size-xl)', marginBottom: 'var(--space-md)', color: 'var(--text-primary)' }}>
          App Locked
        </h2>
        {error && <div style={{ color: 'var(--color-error)', marginBottom: 'var(--space-sm)', fontSize: 'var(--font-size-sm)' }}>{error}</div>}
        <div style={{ marginBottom: 'var(--space-lg)' }}>
          <label htmlFor="lock-password" className="sr-only">Password</label>
          <input id="lock-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
            placeholder="Enter password" autoFocus
            style={{ width: '100%', padding: 'var(--space-md)', borderRadius: 'var(--border-radius-sm)', border: '1px solid var(--border-color)', boxSizing: 'border-box', textAlign: 'center' }} />
        </div>
        <button type="submit" disabled={unlocking || !password} style={{
          width: '100%', padding: 'var(--space-md)', background: 'var(--color-primary)', color: 'white',
          borderRadius: 'var(--border-radius-md)', fontWeight: 'var(--font-weight-semibold)',
          cursor: unlocking ? 'not-allowed' : 'pointer', opacity: unlocking ? 0.7 : 1,
        }}>
          {unlocking ? 'Unlocking...' : 'Unlock'}
        </button>
      </form>
    </div>
  );
}

interface LockMenuProps {
  lock: AppLockInstance;
  onClose: () => void;
}

export function LockMenu({ lock, onClose }: LockMenuProps) {
  const [mode, setMode] = useState<'menu' | 'change' | 'remove'>('menu');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleChangePassword = async () => {
    setBusy(true); setError('');
    try { await lock.changePassword(currentPw, newPw); setMode('menu'); setCurrentPw(''); setNewPw(''); }
    catch (e) { setError(String(e)); } finally { setBusy(false); }
  };

  const handleRemovePassword = async () => {
    setBusy(true); setError('');
    try { await lock.removePassword(currentPw); onClose(); }
    catch (e) { setError(String(e)); } finally { setBusy(false); }
  };

  return (
    <div style={{
      position: 'absolute', top: '100%', right: 0, zIndex: 'var(--z-dropdown)',
      background: 'var(--bg-elevated)', border: '1px solid var(--border-color)',
      borderRadius: 'var(--border-radius-md)', boxShadow: 'var(--shadow-lg)',
      minWidth: 200, padding: 'var(--space-sm)',
    }}>
      {mode === 'menu' && (
        <>
          <button onClick={() => { lock.lock('manual'); onClose(); }} style={{ display: 'block', width: '100%', padding: 'var(--space-sm)', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', color: 'var(--text-primary)' }}>Lock Now</button>
          <button onClick={() => setMode('change')} style={{ display: 'block', width: '100%', padding: 'var(--space-sm)', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', color: 'var(--text-primary)' }}>Change Password</button>
          <button onClick={() => setMode('remove')} style={{ display: 'block', width: '100%', padding: 'var(--space-sm)', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', color: 'var(--color-error)' }}>Remove Password</button>
        </>
      )}
      {mode === 'change' && (
        <div style={{ padding: 'var(--space-sm)', display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
          {error && <div style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)' }}>{error}</div>}
          <input type="password" placeholder="Current password" aria-label="Current password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} style={{ width: '100%', padding: 4, boxSizing: 'border-box' }} />
          <input type="password" placeholder="New password" aria-label="New password" value={newPw} onChange={(e) => setNewPw(e.target.value)} style={{ width: '100%', padding: 4, boxSizing: 'border-box' }} />
          <button disabled={busy} onClick={handleChangePassword} style={{ padding: 4, background: 'var(--color-primary)', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Change</button>
          <button onClick={() => { setMode('menu'); setCurrentPw(''); setNewPw(''); }} style={{ padding: 4, background: 'none', border: 'none', cursor: 'pointer' }}>Cancel</button>
        </div>
      )}
      {mode === 'remove' && (
        <div style={{ padding: 'var(--space-sm)', display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
          {error && <div style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)' }}>{error}</div>}
          <input type="password" placeholder="Current password" aria-label="Current password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} style={{ width: '100%', padding: 4, boxSizing: 'border-box' }} />
          <button disabled={busy} onClick={handleRemovePassword} style={{ padding: 4, background: 'var(--color-error)', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Remove</button>
          <button onClick={() => { setMode('menu'); setCurrentPw(''); }} style={{ padding: 4, background: 'none', border: 'none', cursor: 'pointer' }}>Cancel</button>
        </div>
      )}
    </div>
  );
}
